import { RegisteredComponent } from "@builder.io/sdk-react";
import { DLD_FIELD, DLT_FIELD } from "../commonFields";

export const maturityModelBlockConfig: Omit<RegisteredComponent, "component"> =
  {
    name: "Maturity Model",
    noWrap: true,
    shouldReceiveBuilderProps: {
      builderBlock: true,
      builderComponents: true,
      builderLinkComponent: true,
    },
    canHaveChildren: true,
    childRequirements: {
      message:
        "This component expects an 'Interactive Table Steps' block as its child.",
    },

    defaultChildren: [
      {
        "@type": "@builder.io/sdk:Element",
        component: {
          name: "Interactive Table Steps",
        },
      },
    ],

    inputs: [
      {
        name: "colTitle",
        type: "string",
        localized: true,
        defaultValue: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "ITSM Maturity Levels",
        },
      },
      {
        name: "rowTitle",
        type: "string",
        localized: true,
        defaultValue: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "ITSM Key Areas",
        },
      },

      {
        name: "colHeader",
        type: "list",
        defaultValue: [
          {
            header: {
              value: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Starting",
              },
            },
            hasCards: false,
            content: {
              "@type": "@builder.io/core:LocalizedValue",
              Default:
                "Lorem ipsum dolor sit amet consectetur adipiscing elit. Sit amet consectetur adipiscing elit quisque faucibus ex. Adipiscing elit quisque faucibus ex sapien vitae pellentesque.",
            },
          },
          {
            header: {
              value: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Managing",
              },
            },
            hasCards: true,
          },
          {
            header: {
              value: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Standardizing",
              },
            },
            hasCards: true,
          },
          {
            header: {
              value: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Advancing",
              },
            },
            hasCards: true,
          },
          {
            header: {
              value: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Maturing",
              },
            },
            hasCards: true,
          },
        ],
        subFields: [
          {
            name: "header",
            type: "object",
            subFields: [
              {
                name: "value",
                type: "string",
                localized: true,
                defaultValue: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Category Name",
                },
              },
              { name: "categoryKey", type: "string", required: true },
              { name: "content", type: "richText", localized: true },
              {
                name: "analytics",
                type: "object",
                subFields: [DLT_FIELD, DLD_FIELD],
              },
            ],
          },
          { name: "hasCards", type: "boolean", defaultValue: true },
          {
            name: "content",
            type: "string",
            localized: true,
            helperText: "Content for columns without cards (e.g. Col 1)",
          },
        ],
      },

      {
        name: "rowHeader",
        type: "list",
        defaultValue: [
          {
            value: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Incidents",
            },
            categoryKey: "incidents",
          },
          {
            value: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Assets",
            },
            categoryKey: "assets",
          },
          {
            value: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Experience",
            },
            categoryKey: "experience",
          },
          {
            value: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Services",
            },
            categoryKey: "services",
          },
          {
            value: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Changes",
            },
            categoryKey: "changes",
          },
        ],
        subFields: [
          {
            name: "value",
            type: "string",
            localized: true,
            defaultValue: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Category Name",
            },
          },
          { name: "categoryKey", type: "string", required: true },
          { name: "content", type: "richText", localized: true },
          {
            name: "analytics",
            type: "object",
            subFields: [DLT_FIELD, DLD_FIELD],
          },
        ],
      },

      {
        name: "tableCells",
        type: "list",
        helperText:
          "Grid of table cells. Ensure 'colIndex' matches the column number (0-based).",

        defaultValue: Array.from({ length: 5 }).map((_, rIndex) => ({
          rowCells: Array.from({ length: 4 }).map((_, cIndex) => ({
            header: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: `Feature ${rIndex + 1}-${cIndex + 1}`,
            },
            description: {
              "@type": "@builder.io/core:LocalizedValue",
              Default:
                "Lorem ipsum dolor sit amet consectetur adipiscing elit. Sit amet consectetur adipiscing elit quisque faucibus ex.",
            },
            horizontalCategoryGroup: "Maturity Level",
            verticalCategoryGroup: "Key Area of Focus",
            // IMPORTANT: We start at Column 1 because Col 0 is the text column
            colIndex: cIndex + 1,
          })),
        })),

        subFields: [
          {
            name: "rowCells",
            type: "list",
            subFields: [
              { name: "header", type: "string", localized: true },
              { name: "description", type: "richText", localized: true },
              {
                name: "colIndex",
                type: "number",
                helperText: "0 = First Col, 1 = Second Col, etc.",
              },
              { name: "horizontalCategoryGroup", type: "string" },
              { name: "verticalCategoryGroup", type: "string" },
              {
                name: "challengeKeys",
                type: "list",
                subFields: [{ name: "key", type: "string", required: true }],
              },
              {
                name: "selection",
                type: "object",
                subFields: [
                  {
                    name: "header",
                    type: "string",
                    localized: true,
                    defaultValue: {
                      "@type": "@builder.io/core:LocalizedValue",
                      Default:
                        "Try customizing the model to match your company",
                    },
                  },
                  {
                    name: "ctaText",
                    type: "string",
                    localized: true,
                    defaultValue: {
                      "@type": "@builder.io/core:LocalizedValue",
                      Default: "Save and Close",
                    },
                  },
                  {
                    name: "analytics",
                    type: "object",
                    subFields: [DLT_FIELD, DLD_FIELD],
                  },
                  {
                    name: "options",
                    type: "list",
                    subFields: [
                      {
                        name: "icon",
                        type: "string",
                        enum: ["checkmark", "star"],
                      },
                      {
                        name: "description",
                        type: "string",
                        localized: true,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: "controlsTitle",
        type: "string",
        localized: true,
        defaultValue: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Key",
        },
      },
      {
        name: "controlsOptions",
        type: "list",
        subFields: [
          { name: "icon", type: "string", enum: ["checkmark", "star"] },
          { name: "description", type: "string", localized: true },
        ],
      },
      {
        name: "clearButtonText",
        type: "string",
        localized: true,
        defaultValue: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Clear Model",
        },
      },
      DLT_FIELD,
      DLD_FIELD,
      {
        name: "selectedChallenge",
        type: "object",
        localized: false,
        defaultValue: "{{state.selectedChallenge}}",
      },
      {
        name: "onChallengeChange",
        type: "javascript",
        localized: false,
        helperText:
          "Callback when challenge selection changes. Use to update global state: state.selectedChallenge = value",
      },
      {
        name: "clearModalConfig",
        type: "object",
        subFields: [
          { name: "headline", type: "string", localized: true },
          { name: "description", type: "string", localized: true },
          { name: "backButtonText", type: "string", localized: true },
          {
            name: "backButtonAnalytics",
            type: "object",
            subFields: [DLT_FIELD, DLD_FIELD],
          },
          { name: "startNewButtonText", type: "string", localized: true },
          {
            name: "startNewButtonAnalytics",
            type: "object",
            subFields: [DLT_FIELD, DLD_FIELD],
          },
        ],
      },
    ],
  };
